import { ElementRef } from '@angular/core';
import { ContextMenuComponent, ContextMenuData, ContextMenuService } from '@shared/components';
import { ResultContextMenuItem, TelemetryTrigger } from '../../results/models/results-types';
import { PopupRef } from '@local/ui-infra';
import { KeyboardService } from '@shared/services/keyboard.service';
import { Subject } from 'rxjs';
import { HubService } from 'src/app/bar/services/hub.service';
import { CollectionContextMenuPlace, CollectionOptionsId } from '../models';
import { CollectionContextMenuBuilder } from './collection-context-menu-builder';
import { HomePins, Results } from '@local/client-contracts';
import { ContextMenuInvokeSource } from 'src/app/bar/services/commands/results-item-context-menu-helper';
import { Constants } from '@local/common';
import { WorkspacesService } from 'src/app/bar/services';

export type InvokeModel = { item: ResultContextMenuItem; trigger: TelemetryTrigger };

export class CollectionContextMenuHelper {
  ref: PopupRef<ContextMenuComponent, ContextMenuData> | null;
  items: Array<ResultContextMenuItem> = [];
  invoke$ = new Subject<InvokeModel>();
  contextMenuBuilder: CollectionContextMenuBuilder;

  constructor(
    private contextMenuService: ContextMenuService,
    protected keyboardService: KeyboardService,
    private actionsRef: ElementRef,
    private hubService: HubService,
    protected workspaceService: WorkspacesService
  ) {
    this.contextMenuBuilder = new CollectionContextMenuBuilder(this.hubService, this.keyboardService);
  }

  async open(
    event: MouseEvent,
    model: { pinBy?: HomePins.HomePin[] },
    collectionViewMode: CollectionContextMenuPlace = null,
    isFavorite: boolean,
    removeItems?: CollectionOptionsId[],
    disabledItems?: string[],
    layoutMode?: Results.LayoutType,
    source?: ContextMenuInvokeSource
  ): Promise<PopupRef<ContextMenuComponent, ContextMenuData>> {
    const [disableCollections, disabledFavorites] = await Promise.all([
      this.workspaceService.isFeatureFlagDisabled(Constants.DISABLED_COLLECTIONS_WORKSPACE_FEATURE_FLAG),
      this.workspaceService.isFeatureFlagDisabled(Constants.DISABLED_FAVORITES_WORKSPACE_FEATURE_FLAG),
    ]);
    if (disableCollections) {
      removeItems.push('add_to_collection');
    }
    if (disabledFavorites) {
      removeItems.push('favorite');
    }
    await this.contextMenuBuilder.initItems(isFavorite, model.pinBy, layoutMode);
    const items: ResultContextMenuItem[] = await this.contextMenuBuilder.buildItemsForView(
      collectionViewMode,
      true,
      removeItems,
      disabledItems
    );

    let openAt: { x: number; y: number } = event;
    if (source === 'actions' && this.actionsRef) {
      const { width, left, top, height } = this.actionsRef.nativeElement.getBoundingClientRect();
      if (width && height) {
        openAt = { x: left + width / 2, y: top + height / 2 };
      }
    }

    this.ref = this.contextMenuService.open(
      openAt,
      {
        items,
        onInvoke: (item: ResultContextMenuItem, trigger) => {
          this.invoke$.next({ item, trigger });
        },
      },
      { position: source === 'actions' ? 'below' : 'right' }
    );

    this.ref.destroy$.subscribe(() => {});
    return this.ref;
  }
}
